import type { Condition } from "building-types/conditions/conditionTypes";
import type { DocEntityField } from "building-types/conditions/docEntityFieldTypeDefTypes";
import { DocEntityFieldTypeDefAllowedEntityTypes } from "building-types/conditions/docEntityFieldTypeDefTypes";
import type { Doc } from "building-types/doc";
import { docEntityIdFromString, type DocEntityId } from "building-types/doc/docEntityId";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { Label, LabelableEntityBase } from "building-types/entities/labelTypes";
import type { SelectableBuildingEntityType } from "building-types/selectors/ruleBasedSelectorTypes";
import { SelectionMethod } from "building-types/views/SerializableSheetGeometrySelector";
import type {
  CompiledSelectorFormulasByEntityType,
  SerializableSheetGeometrySelector,
} from "building-types/views/SerializableSheetGeometrySelector";
import type { Table } from "common/data/table";
import type { Mutable } from "common/utils/types";
import type { LabelableBuildingEntityType } from "doc/utils/buildingEntityTools";
import { evalFormula } from "./formula/formulaEval";
import type { Categorized3DGeometry, SheetGeometrySelector, TaggedPlanGeometry } from "./plans/planGeometrySourceTypes";
import {
  buildingEntityIsSelectableByRuleBasedSelector,
  buildingEntityIsLabelable,
  isProp,
  LabelableBuildingEntityTypes,
} from "./utils/buildingEntityTools";
import { labelTableContainsNoLabels, entitiesContainMatchingLabel } from "./utils/labelTools";

export function evalRuleBasedSheetGeometrySelector(
  g: TaggedPlanGeometry | Categorized3DGeometry,
  compiledSelectorFormulas: CompiledSelectorFormulasByEntityType[],
  docLabels: Doc["labels"],
): boolean {
  const { buildingEntity } = g;

  if (!isBuildingEntity(buildingEntity)) {
    return false;
  }

  if (!buildingEntityIsSelectableByRuleBasedSelector(buildingEntity)) {
    return false;
  }

  const formulaEnv = makeEntityFormulaEnv(buildingEntity, docLabels);

  for (const entityFormulas of compiledSelectorFormulas) {
    const formula = entityFormulas[buildingEntity.entityType];

    if (!formula) {
      continue;
    }

    const evalFormulaResult = evalFormula(formula, "", formulaEnv);

    if (evalFormulaResult) {
      return true;
    }
  }

  return false;
}

export function evalLabelBasedSheetGeometrySelector(
  g: TaggedPlanGeometry | Categorized3DGeometry,
  labelHost: LabelableEntityBase,
): boolean {
  if (labelTableContainsNoLabels(labelHost)) {
    return true;
  }

  if (!isBuildingEntity(g.buildingEntity)) {
    return false;
  }

  if (buildingEntityIsLabelable(g.buildingEntity)) {
    return entitiesContainMatchingLabel(g.buildingEntity, labelHost);
  }

  return false;
}

function isObject(obj: unknown): obj is object {
  return !!obj && typeof obj === "object";
}

/**
 * @deprecated This check can be removed once sheet geometry buildingEntity is typed properly
 */
function isBuildingEntity(entity: unknown): entity is BuildingEntityBase<BuildingEntityType> {
  return isObject(entity) && entity.hasOwnProperty("entityType");
}

interface EntityFormulaVariables extends Partial<LabelableEntityBase> {
  readonly verticalOffset?: number;
  readonly entityType: SelectableBuildingEntityType;
  readonly entityLevel?: DocEntityId<"LEVEL">;
}

/**
 * Extracts properties from a building entity for use in a formula evaluation environment.
 */
function buildingEntityFormulaVariables(
  entity: BuildingEntityBase<SelectableBuildingEntityType>,
): EntityFormulaVariables {
  const entityVariables: Mutable<EntityFormulaVariables> = { entityType: entity.entityType };

  if (buildingEntityIsLabelable(entity)) {
    entityVariables.labels = entity.labels;
  }

  if (isProp(entity) && entity.room) {
    // TODO (paulscrugham): we should just get this value from the verticalOffset field on the doc Prop entity.
    entityVariables.verticalOffset = entity.z - entity.room.level.floorElevation;
    entityVariables.entityLevel = entity.room.level.id
      ? docEntityIdFromString("LEVEL", entity.room.level.id)
      : undefined;
  }

  return entityVariables;
}

/**
 * Returns a function used to evaluate a formula in the context of a building entity.
 */
function makeEntityFormulaEnv(entity: BuildingEntityBase<SelectableBuildingEntityType>, labels: Table<Label>) {
  const entityVariables = buildingEntityFormulaVariables(entity);
  return (id: string) => {
    if (id === "entityType" && entityVariables.entityType !== undefined) {
      return entityVariables.entityType;
    }

    if (id === "verticalOffset" && entityVariables.verticalOffset !== undefined) {
      return entityVariables.verticalOffset;
    }

    if (id === "entityLevel" && entityVariables.entityLevel !== undefined) {
      return entityVariables.entityLevel;
    }

    if (labels[id] && entityVariables.labels !== undefined) {
      return entityVariables.labels?.[id] ? "true" : "false";
    }

    return undefined;
  };
}

/**
 * Chunks an array of rules into groups of rules that are connected by an "and" operator.
 */
export function chunkSelectorRulesByLogicalAnd(rules: Condition[]): Condition[][] {
  if (rules.length === 0) {
    return [];
  }

  if (rules.length === 1) {
    return [rules];
  }

  const chunkedRules: Condition[][] = [];

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    if (rule.connective === "and" && chunkedRules.length > 0) {
      chunkedRules[chunkedRules.length - 1].push(rule);
    } else {
      chunkedRules.push([rule]);
    }
  }

  return chunkedRules;
}

/**
 * Checks if every rule in an array of rules is valid for a given entity type.
 */
export function rulesChunkIsValid(rulesChunk: Condition[], entityType: BuildingEntityType): boolean {
  return rulesChunk.every((rule) => {
    const allowedEntityFieldTypes = DocEntityFieldTypeDefAllowedEntityTypes[rule.typeDefId as DocEntityField];

    if (allowedEntityFieldTypes) {
      return allowedEntityFieldTypes.includes(entityType);
    }

    return LabelableBuildingEntityTypes.includes(entityType as LabelableBuildingEntityType);
  });
}

export function evalSerializableSheetGeometrySelector(
  g: TaggedPlanGeometry | Categorized3DGeometry,
  selector: SerializableSheetGeometrySelector,
): boolean {
  switch (selector.selectionMethod) {
    case SelectionMethod.ALL:
      return true;
    case SelectionMethod.BY_LABEL:
      return evalLabelBasedSheetGeometrySelector(g, selector);
    case SelectionMethod.BY_SELECTOR:
      return evalRuleBasedSheetGeometrySelector(g, selector.formulas, selector.docLabels);
  }
}

export function evalSheetGeometrySelector(
  g: TaggedPlanGeometry | Categorized3DGeometry,
  selector: SheetGeometrySelector | SerializableSheetGeometrySelector,
): boolean {
  return typeof selector === "function" ? selector(g) : evalSerializableSheetGeometrySelector(g, selector);
}
